import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useSwipe } from '../utils/useSwipe';
import { api } from '../utils/api';
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';

const LAB_RANGES = {
  a1c:               { label: 'A1C',               unit: '%',     good: [0, 5.6],   warn: [5.6, 6.4],  bad: [6.4, 99],   normal: '<5.6%'  },
  fasting_glucose:   { label: 'Fasting Glucose',   unit: 'mg/dL', good: [0, 99],    warn: [100, 125],  bad: [126, 999],  normal: '<100'   },
  triglycerides:     { label: 'Triglycerides',      unit: 'mg/dL', good: [0, 149],   warn: [150, 199],  bad: [200, 999],  normal: '<149'   },
  hdl:               { label: 'HDL',               unit: 'mg/dL', good: [60, 999],  warn: [40, 60],    bad: [0, 40],     normal: '>60'    },
  ldl:               { label: 'LDL',               unit: 'mg/dL', good: [0, 99],    warn: [100, 159],  bad: [160, 999],  normal: '<100'   },
  total_cholesterol: { label: 'Total Cholesterol', unit: 'mg/dL', good: [0, 199],   warn: [200, 239],  bad: [240, 999],  normal: '<200'   },
  systolic:          { label: 'Systolic BP',       unit: 'mmHg',  good: [0, 119],   warn: [120, 129],  bad: [130, 999],  normal: '<120'   },
  diastolic:         { label: 'Diastolic BP',      unit: 'mmHg',  good: [0, 79],    warn: [80, 89],    bad: [90, 999],   normal: '<80'    },
};

const LAB_CHART_KEYS = [
  { key: 'a1c',               color: '#B8922A', label: 'A1C (%)'       },
  { key: 'fasting_glucose',   color: '#B02A2A', label: 'Glucose'       },
  { key: 'triglycerides',     color: '#7A3D2C', label: 'Triglycerides' },
  { key: 'hdl',               color: '#2C7A4B', label: 'HDL'           },
  { key: 'ldl',               color: '#5A4A1A', label: 'LDL'           },
  { key: 'total_cholesterol', color: '#1A3A5A', label: 'Total Chol.'   },
  { key: 'systolic',          color: '#8B2252', label: 'Systolic BP'   },
  { key: 'diastolic',         color: '#C4527A', label: 'Diastolic BP'  },
];

const WEIGHT_PERIODS = [
  { label: '1Y',  days: 365 },
  { label: '6M',  days: 182 },
  { label: '90D', days: 90  },
  { label: '30D', days: 30  },
  { label: 'All', days: null },
];

const STEPS_PERIODS = [
  { label: '7D',  days: 7  },
  { label: '30D', days: 30 },
  { label: '90D', days: 90 },
];

const STEP_TIERS = [
  { key: 'miss',      label: 'Miss',      min: 0,     max: 4999,      color: '#D42B2B', bg: '#FDEEED', border: '#D42B2B44' },
  { key: 'minimum',   label: 'Minimum',   min: 5000,  max: 7999,      color: '#E07B00', bg: '#FEF4E6', border: '#E07B0044' },
  { key: 'preferred', label: 'Preferred', min: 8000,  max: 9999,      color: '#5CB85C', bg: '#EEF8EE', border: '#5CB85C44' },
  { key: 'excellent', label: 'Excellent', min: 10000, max: Infinity,  color: '#1A7A1A', bg: '#E6F4E6', border: '#1A7A1A44' },
];

const WEIGHT_COLOR    = '#C17A5A';
const WEIGHT_COLOR_BG = '#FDF0EF';

const WEIGHT_MILESTONES = [
  { label: '10% Loss', weight: 263, color: '#E07B00' },
  { label: 'Halfway',  weight: 240, color: '#5CB85C' },
  { label: 'Goal',     weight: 200, color: '#1A7A1A' },
];

function getLabStatus(key, value) {
  if (value == null) return 'gray';
  const r = LAB_RANGES[key];
  if (!r) return 'gray';
  const v = parseFloat(value);
  if (v >= r.good[0] && v <= r.good[1]) return 'green';
  if (v >= r.warn[0] && v <= r.warn[1]) return 'yellow';
  return 'red';
}

function getStepTier(steps) {
  return STEP_TIERS.find(t => steps >= t.min && steps <= t.max) || STEP_TIERS[0];
}

const STATUS_COLORS = { green: '#2C7A4B', yellow: '#B8922A', red: '#B02A2A', gray: '#9E9890' };
const STATUS_BG     = { green: '#EEF7F2', yellow: '#FBF6EA', red: '#FBF0F0', gray: '#F5F2EC' };
const STATUS_BORDER = { green: '#2C7A4B33', yellow: '#B8922A33', red: '#B02A2A33', gray: '#9E989033' };

function ProgressRing({ pct, color, size, strokeWidth, label, sublabel }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.min(1, pct / 100));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', display: 'block' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#EBEBEB" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
        <div style={{ fontSize: size > 130 ? 48 : 22, fontWeight: 900, color, lineHeight: 1 }}>{label}</div>
        {sublabel && <div style={{ fontSize: 11, color: '#aaa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{sublabel}</div>}
      </div>
    </div>
  );
}

const sectionLabel = {
  fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
  textTransform: 'uppercase', color: '#aaa',
};

export default function Health({ data, updateData }) {
  const weight = data.weight || [];
  const labs   = data.labs   || [];

  const [weightData, setWeightData] = useState(weight);
  const [stepsData,  setStepsData]  = useState([]);
  const [labsData,   setLabsData]   = useState(labs);
  const [showLogWeight, setShowLogWeight] = useState(false);

  useEffect(() => {
    const h = { 'X-API-Key': process.env.REACT_APP_API_KEY };
    const base = 'https://whitcomb-dashboard-api-production.up.railway.app';
    Promise.all([
      fetch(base + '/api/health/weight', { headers: h }).then(r => r.json()),
      fetch(base + '/api/health/labs',   { headers: h }).then(r => r.json()),
    ]).then(([weightRows, labRows]) => {
      setWeightData(weightRows);
      setStepsData(weightRows.filter(r => r.steps != null && r.steps > 0));
      if (labRows?.length) setLabsData(labRows);
    }).catch(() => {});
  }, []);

  const [sectionIdx, setSectionIdx] = useState(0);
  const activeSection = ['weight','steps','labs'][sectionIdx];
  const setActiveSection = (id) => setSectionIdx(['weight','steps','labs'].indexOf(id));
  const [slideDir, setSlideDir] = useState(null);
  const goLeft  = useCallback(() => { setSlideDir('left');  setSectionIdx(i => Math.max(0, i - 1)); }, []);
  const goRight = useCallback(() => { setSlideDir('right'); setSectionIdx(i => Math.min(2, i + 1)); }, []);
  useSwipe(goRight, goLeft);
  const [weightInput,   setWeightInput]   = useState('');
  const [saving,        setSaving]        = useState(false);
  const [savingLabs,    setSavingLabs]    = useState(false);
  const [weightPeriod,  setWeightPeriod]  = useState('1Y');
  const [stepsPeriod,   setStepsPeriod]   = useState(30);
  const [labForm, setLabForm] = useState({
    date: new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    a1c: '', fasting_glucose: '', triglycerides: '',
    hdl: '', ldl: '', systolic: '', diastolic: '', total_cholesterol: '',
  });

  const today       = new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
  const startWeight = 292.2;
  const goalWeight  = 200;

  const currentWeight = weightData.length > 0
    ? parseFloat([...weightData].filter(w => w.weight != null).sort((a, b) => a.date.localeCompare(b.date)).at(-1)?.weight)
    : null;
  const lost    = currentWeight ? startWeight - currentWeight : 0;
  const goalPct = Math.min(100, Math.max(0, (lost / (startWeight - goalWeight)) * 100));

  // Steps derived values
  const stepsCutoff = new Date();
  stepsCutoff.setDate(stepsCutoff.getDate() - stepsPeriod);
  const stepsCutoffStr = stepsCutoff.toISOString().split('T')[0];
  const filteredSteps = stepsData.filter(e => e.date >= stepsCutoffStr);
  const latestStep    = filteredSteps.length ? filteredSteps[filteredSteps.length - 1] : null;
  const latestStepCount = latestStep ? (latestStep.steps ?? 0) : 0;
  const latestStepTier  = getStepTier(latestStepCount);
  const stepsRingPct    = Math.min(100, (latestStepCount / 10000) * 100);
  const tierCounts = STEP_TIERS.map(t => ({
    ...t,
    count: filteredSteps.filter(e => (e.steps ?? 0) >= t.min && (e.steps ?? 0) <= t.max).length,
  }));
  const stepsChartData = filteredSteps.map(e => ({
    steps: e.steps ?? 0,
    label: new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));

  // Labs derived values — most recent value per marker independently
  const labKeys        = Object.keys(LAB_RANGES);
  const latestLabs     = labsData.length > 0
    ? labKeys.reduce((acc, k) => {
        const sorted = [...labsData].filter(l => l[k] != null).sort((a, b) => a.date.localeCompare(b.date));
        acc[k] = sorted.length > 0 ? sorted[sorted.length - 1][k] : null;
        return acc;
      }, { date: labsData[labsData.length - 1]?.date })
    : null;
  const labTotal       = latestLabs ? labKeys.filter(k => latestLabs[k] != null).length : 0;
  const labGood        = latestLabs ? labKeys.filter(k => getLabStatus(k, latestLabs[k]) === 'green').length : 0;
  const labWarn        = latestLabs ? labKeys.filter(k => getLabStatus(k, latestLabs[k]) === 'yellow').length : 0;
  const labBad         = latestLabs ? labKeys.filter(k => getLabStatus(k, latestLabs[k]) === 'red').length : 0;
  const labRingPct     = labTotal > 0 ? Math.round((labGood / labTotal) * 100) : 0;
  const labRingColor   = labBad > 0 ? '#B02A2A' : labWarn > 0 ? '#B8922A' : '#2C7A4B';

  const filteredWeightData = useMemo(() => {
    const period = WEIGHT_PERIODS.find(p => p.label === weightPeriod);
    if (!period || !period.days)
      return weightData.filter(w => w.date.match(/^\d{4}-\d{2}-\d{2}$/)).sort((a, b) => a.date.localeCompare(b.date));
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - period.days);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return weightData
      .filter(w => w.date >= cutoffStr && w.date.match(/^\d{4}-\d{2}-\d{2}$/))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [weightData, weightPeriod]);

  const weightChartData = filteredWeightData.map(w => ({
    date: w.date, fullDate: w.date, weight: parseFloat(w.weight),
  }));

  const labsChartData = labsData.map(l => ({
    date:              l.date,
    a1c:               l.a1c             ? parseFloat(l.a1c)             : null,
    fasting_glucose:   l.fasting_glucose ? parseFloat(l.fasting_glucose) : null,
    triglycerides:     l.triglycerides   ? parseFloat(l.triglycerides)   : null,
    hdl:               l.hdl             ? parseFloat(l.hdl)             : null,
    ldl:               l.ldl             ? parseFloat(l.ldl)             : null,
    total_cholesterol: l.total_cholesterol ? parseFloat(l.total_cholesterol) : null,
    systolic:          l.systolic          ? parseFloat(l.systolic)          : null,
    diastolic:         l.diastolic         ? parseFloat(l.diastolic)         : null,
  }));

  const saveWeight = async () => {
    const val = parseFloat(weightInput);
    if (isNaN(val)) return;
    setSaving(true);
    await api.saveWeight({ date: today, weight: val });
    const newWeight = [...weightData.filter(w => w.date !== today), { date: today, weight: val }];
    newWeight.sort((a, b) => a.date.localeCompare(b.date));
    setWeightData(newWeight);
    updateData('weight', newWeight);
    setWeightInput('');
    setSaving(false);
    setShowLogWeight(false);
  };

  const saveLabs = async () => {
    setSavingLabs(true);
    try {
      await api.saveLabs({
        date:             labForm.date,
        a1c:              labForm.a1c              || null,
        fastingGlucose:   labForm.fasting_glucose  || null,
        triglycerides:    labForm.triglycerides     || null,
        hdl:              labForm.hdl               || null,
        ldl:              labForm.ldl               || null,
        systolic:         labForm.systolic          || null,
        diastolic:        labForm.diastolic         || null,
        totalCholesterol: labForm.total_cholesterol || null,
      });
      const newLabs = [...labs.filter(l => l.date !== labForm.date), { ...labForm }];
      newLabs.sort((a, b) => a.date.localeCompare(b.date));
      updateData('labs', newLabs);
    } catch (e) {
      console.error('saveLabs error:', e);
      alert('Failed to save labs: ' + e.message);
    } finally {
      setSavingLabs(false);
    }
  };

  const pillBtn = (active) => ({
    padding: '6px 16px', borderRadius: 99,
    border: active ? 'none' : '1px solid #EBEBEB',
    cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    background: active ? '#172A3A' : 'white',
    color: active ? 'white' : '#172A3A',
    transition: 'all 0.15s',
  });

  const periodPillBtn = (active) => ({
    padding: '6px 16px', borderRadius: 99, border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
    background: active ? '#172A3A' : '#F0F0F0',
    color: active ? 'white' : '#666',
    transition: 'all 0.15s',
  });

  const sections = [
    { id: 'weight', label: '⚖️ Weight' },
    { id: 'steps',  label: '👟 Steps'  },
    { id: 'labs',   label: '🧪 Labs'   },
  ];

  const StepsTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const steps = payload[0].value;
    const tier = getStepTier(steps);
    return (
      <div style={{ background: 'white', border: `1.5px solid ${tier.color}`, borderRadius: 8, padding: '8px 12px', fontSize: 12 }}>
        <div style={{ fontWeight: 700, color: tier.color }}>{steps.toLocaleString()} steps</div>
        <div style={{ color: '#888' }}>{payload[0].payload.label}</div>
      </div>
    );
  };

  return (
    <div>
      <style>{`
        @media (max-width: 600px) {
          .c2-wrap { flex-direction: column !important; }
          .c2-left { flex: 0 0 100% !important; width: 100% !important; border-right: none !important; border-bottom: 1px solid #EBEBEB !important; padding: 28px 24px !important; }
        }
      `}</style>
      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 28, flexWrap: 'wrap' }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)} style={pillBtn(activeSection === s.id)}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── WEIGHT ─────────────────────────────────── */}
      {activeSection === 'weight' && (
        <div style={{ padding: '0 0 40px' }}>
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', overflow: 'hidden', marginBottom: 24 }}>
            <div className="c2-wrap" style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #EBEBEB' }}>

              {/* LEFT */}
              <div className="c2-left" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '32px 40px', borderRight: '1px solid #EBEBEB', flex: '0 0 320px', width: 320, boxSizing: 'border-box' }}>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>⚖️ Weight · goal {goalWeight} lbs</div>
                <ProgressRing pct={goalPct} color={WEIGHT_COLOR} size={280} strokeWidth={22}
                  label={`${goalPct.toFixed(0)}%`} sublabel="to goal" />
                <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, letterSpacing: '-2px', color: WEIGHT_COLOR, marginTop: 16, marginBottom: 4 }}>
                  {currentWeight ? currentWeight.toFixed(1) : '—'}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: WEIGHT_COLOR, marginBottom: 20 }}>lbs · current</div>
                <button onClick={() => setShowLogWeight(v => !v)} style={{
                  padding: '10px 28px', borderRadius: 99, border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                  background: '#172A3A', color: 'white', transition: 'all 0.15s',
                }}>+ Log Weight</button>
              </div>

              {/* RIGHT */}
              <div style={{ flex: 1, minWidth: 0, padding: '28px 28px 24px' }}>
                {showLogWeight && (
                  <div style={{ background: '#F8F8F8', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid #EBEBEB' }}>
                    <div style={{ fontSize: 12, color: '#aaa', marginBottom: 8 }}>Today's weight (lbs)</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input type="number" placeholder="e.g. 276.0" value={weightInput}
                        onChange={e => setWeightInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && saveWeight()} autoFocus
                        style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #EBEBEB', fontSize: 13 }} />
                      <button onClick={saveWeight} disabled={saving}
                        style={{ padding: '6px 16px', borderRadius: 99, border: 'none', background: '#2C7A4B', color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
                        {saving ? '...' : 'Save'}
                      </button>
                      <button onClick={() => setShowLogWeight(false)}
                        style={{ padding: '6px 12px', borderRadius: 99, border: '1px solid #EBEBEB', background: 'white', color: '#aaa', cursor: 'pointer', fontSize: 13 }}>✕</button>
                    </div>
                    <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>Also auto-updated by your iOS Shortcut via Apple Health.</div>
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
                  {[
                    { label: 'Lost',  value: `${lost.toFixed(1)}` },
                    { label: 'To Go', value: currentWeight ? `${(currentWeight - goalWeight).toFixed(1)}` : '—' },
                    { label: 'Start', value: `${startWeight}` },
                  ].map(s => (
                    <div key={s.label} style={{ background: WEIGHT_COLOR_BG, border: `1.5px solid ${WEIGHT_COLOR}33`, borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: WEIGHT_COLOR, lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
                      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ ...sectionLabel, marginBottom: 10 }}>Milestones</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                  {WEIGHT_MILESTONES.map(m => {
                    const reached = currentWeight && currentWeight <= m.weight;
                    const pct = Math.min(100, Math.max(0, ((startWeight - (currentWeight || startWeight)) / (startWeight - m.weight)) * 100));
                    const color = reached ? '#2C7A4B' : m.color;
                    return (
                      <div key={m.label} style={{ background: reached ? '#EEF7F2' : '#F8F8F8', border: `1.5px solid ${color}33`, borderRadius: 10, padding: '10px 10px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color, marginBottom: 4 }}>{m.label}</div>
                        <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1, color, marginBottom: 4 }}>{reached ? '✓' : `${pct.toFixed(0)}%`}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{reached ? 'Reached!' : `${m.weight} lbs`}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {weightChartData.length > 1 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', padding: '20px 20px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <div style={sectionLabel}>Weight Trend</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {WEIGHT_PERIODS.map(p => (
                    <button key={p.label} onClick={() => setWeightPeriod(p.label)} style={periodPillBtn(weightPeriod === p.label)}>{p.label}</button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={weightChartData} margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false}
                    tickFormatter={v => v?.substring(5)} interval={Math.floor(weightChartData.length / 6)} />
                  <YAxis domain={['auto', 'auto']} tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false} />
                  <Tooltip formatter={v => [`${v} lbs`, 'Weight']}
                    labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label}
                    contentStyle={{ fontSize: 12, border: '1px solid #EBEBEB', borderRadius: 8 }} />
                  <ReferenceLine y={goalWeight} stroke="#1A7A1A" strokeDasharray="4 4" />
                  <Line type="monotone" dataKey="weight" stroke="#C17A5A" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ── STEPS ─────────────────────────────────── */}
      {activeSection === 'steps' && (
        <div style={{ padding: '0 0 40px' }}>
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', overflow: 'hidden', marginBottom: 24 }}>
            <div className="c2-wrap" style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #EBEBEB' }}>

              {/* LEFT */}
              <div className="c2-left" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '32px 40px', borderRight: '1px solid #EBEBEB', flex: '0 0 320px', width: 320, boxSizing: 'border-box' }}>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>👟 Steps · goal 10,000/day</div>
                <ProgressRing pct={stepsRingPct} color={latestStepTier.color} size={280} strokeWidth={22}
                  label={`${stepsRingPct.toFixed(0)}%`} sublabel="of 10k" />
                <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, letterSpacing: '-2px', color: latestStepTier.color, marginTop: 16, marginBottom: 4 }}>
                  {latestStepCount.toLocaleString()}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: latestStepTier.color, marginBottom: 20 }}>
                  steps · {latestStepTier.label}
                </div>
                <div style={{ fontSize: 12, color: '#aaa' }}>
                  {latestStep ? new Date(latestStep.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—'}
                </div>
              </div>

              {/* RIGHT */}
              <div style={{ flex: 1, minWidth: 0, padding: '28px 28px 24px' }}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
                  {STEPS_PERIODS.map(p => (
                    <button key={p.days} onClick={() => setStepsPeriod(p.days)} style={periodPillBtn(stepsPeriod === p.days)}>{p.label}</button>
                  ))}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 20 }}>
                  {tierCounts.map(t => (
                    <div key={t.key} style={{ background: t.bg, border: `1.5px solid ${t.border}`, borderRadius: 10, padding: '12px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: t.color, lineHeight: 1, marginBottom: 4 }}>{t.count}</div>
                      <div style={{ fontSize: 10, color: '#aaa', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{t.label}</div>
                    </div>
                  ))}
                </div>
                {stepsChartData.length > 0 && (
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={stepsChartData} margin={{ top: 4, right: 0, left: -20, bottom: 0 }} barCategoryGap={1}>
                      <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false}
                        interval={stepsPeriod === 7 ? 0 : Math.floor(stepsChartData.length / 5)} />
                      <YAxis tick={{ fontSize: 10, fill: '#aaa' }} axisLine={false} tickLine={false}
                        tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                      <Tooltip content={<StepsTooltip />} cursor={{ fill: '#F5F5F5' }} />
                      <Bar dataKey="steps" radius={[2, 2, 0, 0]}>
                        {stepsChartData.map((entry, i) => <Cell key={i} fill={getStepTier(entry.steps).color} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LABS ─────────────────────────────────────── */}
      {activeSection === 'labs' && (
        <div style={{ padding: '0 0 40px' }}>
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', overflow: 'hidden', marginBottom: 24 }}>
            <div className="c2-wrap" style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid #EBEBEB' }}>

              {/* LEFT */}
              <div className="c2-left" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '32px 40px', borderRight: '1px solid #EBEBEB', flex: '0 0 320px', width: 320, boxSizing: 'border-box' }}>
                <div style={{ fontSize: 12, color: '#aaa', marginBottom: 16 }}>🧪 Labs · {latestLabs ? latestLabs.date : '—'}</div>
                <ProgressRing pct={labRingPct} color={labRingColor} size={280} strokeWidth={22}
                  label={`${labGood}/${labTotal}`} sublabel="in range" />
                <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, letterSpacing: '-2px', color: labRingColor, marginTop: 16, marginBottom: 4 }}>
                  {labTotal > 0 ? `${labRingPct}%` : '—'}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: labRingColor, marginBottom: 20 }}>markers in range</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', maxWidth: 160 }}>
                  {[
                    { label: '✓ Good',    count: labGood, color: '#2C7A4B', bg: '#EEF7F2' },
                    { label: '⚠ Warning', count: labWarn, color: '#B8922A', bg: '#FBF6EA' },
                    { label: '✕ High',    count: labBad,  color: '#B02A2A', bg: '#FBF0F0' },
                  ].map(s => (
                    <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: s.bg, borderRadius: 8, padding: '6px 12px' }}>
                      <span style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: s.color }}>{s.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* RIGHT */}
              <div style={{ flex: 1, minWidth: 0, padding: '28px 28px 24px' }}>
                {latestLabs && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                    {Object.entries(LAB_RANGES).map(([key, range]) => {
                      const val = latestLabs[key];
                      const status = getLabStatus(key, val);
                      const color = STATUS_COLORS[status];
                      return (
                        <div key={key} style={{ background: STATUS_BG[status], border: `1.5px solid ${STATUS_BORDER[status]}`, borderRadius: 10, padding: '10px 12px' }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color, marginBottom: 4 }}>{range.label}</div>
                          <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1, color, marginBottom: 2 }}>{val != null ? val : '—'}</div>
                          <div style={{ fontSize: 10, color: '#888' }}>{range.unit} · {range.normal}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {labsData.length > 1 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', padding: '20px 20px 12px', marginBottom: 24 }}>
              <div style={{ ...sectionLabel, marginBottom: 16 }}>Lab Trends Over Time</div>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={labsChartData} margin={{ top: 4, right: 24, left: 8, bottom: 0 }}>
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#aaa' }} axisLine={false} tickLine={false} tickFormatter={v => v?.substring(5)} />
                  <YAxis hide />
                  <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #EBEBEB', borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
                  {LAB_CHART_KEYS.map(k => (
                    <Line key={k.key} type="monotone" dataKey={k.key} name={k.label} stroke={k.color} strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {labsData.length > 1 && (
            <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', marginBottom: 24, overflow: 'hidden' }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Date</th><th>A1C</th><th>Glucose</th><th>Triglycerides</th><th>HDL</th><th>LDL</th><th>Total Chol.</th><th>BP</th></tr></thead>
                  <tbody>
                    {[...labsData].reverse().map(l => (
                      <tr key={l.date}>
                        <td style={{ fontWeight: 500 }}>{l.date}</td>
                        <td>{l.a1c ?? '—'}</td>
                        <td>{l.fasting_glucose ?? '—'}</td>
                        <td>{l.triglycerides ?? '—'}</td>
                        <td>{l.hdl ?? '—'}</td>
                        <td>{l.ldl ?? '—'}</td>
                        <td>{l.total_cholesterol ?? '—'}</td>
                        <td>{l.systolic && l.diastolic ? `${l.systolic}/${l.diastolic}` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #EBEBEB', padding: '20px 24px' }}>
            <div style={{ ...sectionLabel, marginBottom: 16 }}>Log Lab Results</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, color: '#aaa', display: 'block', marginBottom: 4 }}>Date</label>
                <input type="date" value={labForm.date} onChange={e => setLabForm(p => ({ ...p, date: e.target.value }))} />
              </div>
              {Object.entries(LAB_RANGES).map(([key, range]) => (
                <div key={key}>
                  <label style={{ fontSize: 12, color: '#aaa', display: 'block', marginBottom: 4 }}>{range.label} ({range.unit})</label>
                  <input type="number" step="0.1" placeholder="—" value={labForm[key] || ''}
                    onChange={e => setLabForm(p => ({ ...p, [key]: e.target.value }))} />
                </div>
              ))}
            </div>
            <button onClick={saveLabs} disabled={savingLabs} style={{
              padding: '8px 20px', borderRadius: 99, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit', background: '#172A3A', color: 'white',
            }}>
              {savingLabs ? 'Saving...' : 'Save Lab Results'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
